"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CoinGeckoMarketService = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const logger_1 = require("@shapeshiftoss/logger");
const types_1 = require("@shapeshiftoss/types");
const dayjs_1 = __importDefault(require("dayjs"));
const config_1 = require("../config");
const bignumber_1 = require("../utils/bignumber");
const isValidDate_1 = require("../utils/isValidDate");
const rateLimiters_1 = require("../utils/rateLimiters");
const logger = new logger_1.Logger({
    namespace: ['market-service', 'coingecko'],
    level: process.env.LOG_LEVEL,
});
const axios = (0, rateLimiters_1.rateLimitedAxios)(config_1.RATE_LIMIT_THRESHOLDS_PER_MINUTE.COINGECKO);
class CoinGeckoMarketService {
    constructor(args) {
        this.maxPerPage = 250;
        this.defaultCount = 2500;
        this.APIKey = args.coinGeckoAPIKey;
        const { baseUrl, maybeApiKeyQueryParam } = caip_1.adapters.makeCoingeckoUrlParts(this.APIKey);
        this.baseUrl = baseUrl;
        this.maybeApiKeyQueryParam = maybeApiKeyQueryParam;
    }
    async findAll(args) {
        var _a;
        const count = (_a = args === null || args === void 0 ? void 0 : args.count) !== null && _a !== void 0 ? _a : this.defaultCount;
        const perPage = count < this.maxPerPage ? count : this.maxPerPage;
        const pages = Math.ceil((0, bignumber_1.bnOrZero)(count).div(perPage).toNumber());
        const pageCount = Array(pages)
            .fill(0)
            .map((_v, i) => i + 1);
        try {
            const marketData = await Promise.all(pageCount.map(async (page) => {
                const { data } = await axios.get(`${this.baseUrl}/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${perPage}&page=${page}&sparkline=false${this.maybeApiKeyQueryParam}`);
                return data !== null && data !== void 0 ? data : [];
            }));
            return marketData.flat().reduce((prev, asset) => {
                const assetIds = caip_1.adapters.coingeckoToAssetIds(asset.id);
                if (!assetIds)
                    return prev;
                assetIds.forEach((assetId) => {
                    var _a, _b, _c;
                    prev[assetId] = {
                        price: asset.current_price.toString(),
                        marketCap: asset.market_cap.toString(),
                        volume: asset.total_volume.toString(),
                        changePercent24Hr: asset.price_change_percentage_24h,
                        supply: asset.circulating_supply.toString(),
                        maxSupply: (_b = (_a = asset.max_supply) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : (_c = asset.total_supply) === null || _c === void 0 ? void 0 : _c.toString(),
                    };
                });
                return prev;
            }, {});
        }
        catch (e) {
            return {};
        }
    }
    async findByAssetId({ assetId }) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!caip_1.adapters.assetIdToCoingecko(assetId))
            return null;
        const url = caip_1.adapters.makeCoingeckoAssetUrl(assetId, this.APIKey);
        if (!url)
            return null;
        try {
            const { data } = await axios.get(url);
            const currency = 'usd';
            const marketData = data === null || data === void 0 ? void 0 : data.market_data;
            if (!marketData)
                return null;
            /* max_supply may be null on coingecko while available on other sources (coincap)
            hence choosing to take value from total_supply if existing
            Also a lot of time when max_supply is null, total_supply is the maximum supply on coingecko
            We can reassess in the future the degree of precision we want on that field */
            return {
                price: (0, bignumber_1.bnOrZero)((_a = marketData.current_price) === null || _a === void 0 ? void 0 : _a[currency]).toString(),
                marketCap: (0, bignumber_1.bnOrZero)((_b = marketData.market_cap) === null || _b === void 0 ? void 0 : _b[currency]).toString(),
                changePercent24Hr: marketData.price_change_percentage_24h,
                volume: (0, bignumber_1.bnOrZero)((_c = marketData.total_volume) === null || _c === void 0 ? void 0 : _c[currency]).toString(),
                supply: (0, bignumber_1.bnOrZero)(marketData.circulating_supply).toString(),
                maxSupply: (_g = (_e = (_d = marketData.max_supply) === null || _d === void 0 ? void 0 : _d.toString()) !== null && _e !== void 0 ? _e : (_f = marketData.total_supply) === null || _f === void 0 ? void 0 : _f.toString()) !== null && _g !== void 0 ? _g : undefined,
            };
        }
        catch (e) {
            console.warn(e);
            throw new Error('CoinGeckoMarketService(findByAssetId): error fetching market data');
        }
    }
    async findPriceHistoryByAssetId({ assetId, timeframe, }) {
        const fnLogger = logger.child({ fn: 'findPriceHistoryByAssetId', assetId, timeframe });
        if (!caip_1.adapters.assetIdToCoingecko(assetId))
            return [];
        const url = caip_1.adapters.makeCoingeckoAssetUrl(assetId, this.APIKey);
        if (!url)
            return [];
        try {
            const end = (0, dayjs_1.default)().startOf('minute');
            const start = (() => {
                switch (timeframe) {
                    case types_1.HistoryTimeframe.HOUR:
                        return end.subtract(1, 'hour');
                    case types_1.HistoryTimeframe.DAY:
                        return end.subtract(1, 'day');
                    case types_1.HistoryTimeframe.WEEK:
                        return end.subtract(1, 'week');
                    case types_1.HistoryTimeframe.MONTH:
                        return end.subtract(1, 'month');
                    case types_1.HistoryTimeframe.YEAR:
                        return end.subtract(1, 'year');
                    case types_1.HistoryTimeframe.ALL:
                        return end.subtract(20, 'years');
                    default:
                        return end;
                }
            })();
            const currency = 'usd';
            const from = start.valueOf() / 1000;
            const to = end.valueOf() / 1000;
            const [baseUrl, apiKeyQueryParam = ''] = url.split('?');
            const { data: historyData } = await axios.get(`${baseUrl}/market_chart/range?vs_currency=${currency}&from=${from}&to=${to}${apiKeyQueryParam}`);
            return historyData.prices.reduce((prev, data) => {
                const [date, price] = data;
                if (!(0, isValidDate_1.isValidDate)(date)) {
                    fnLogger.error('invalid date');
                    return prev;
                }
                if ((0, bignumber_1.bn)(price).isNaN()) {
                    fnLogger.error('invalid price');
                    return prev;
                }
                prev.push({ date, price });
                return prev;
            }, []);
        }
        catch (err) {
            fnLogger.error(err, 'failed to fetch price history');
            throw new Error('CoinGeckoMarketService(findPriceHistoryByAssetId): error fetching price history');
        }
    }
}
exports.CoinGeckoMarketService = CoinGeckoMarketService;
